<template>
    <tr>
        <td>
            <toggle v-model="item.includeInAnnualCatalog"></toggle>
        </td>
        <td>
            <div>{{ item.theme }}</div>
        </td>
        <td>
            <input class="form-control" v-model="item.alternativeName"></td>
        <td>
            <dynamic-dropdown api-url="/"
                              :items-from-parent="thematicAreasDropdownItems"
                              :required="item.includeInAnnualCatalog ? true : false"
                              :field-name="$t('thematicArea.thematicArea')"
                              v-model="item.thematicAreaId"></dynamic-dropdown>
        </td>
        <td>
            <nomenclature-dropdown :load-by-parent-sort="true"
                                   :parent-sort="nomenclature.LegalArea"
                                   :required="item.includeInAnnualCatalog ? true : false"
                                   :field-name="$t('planning.legalArea')"
                                   v-model="item.legalAreaId"></nomenclature-dropdown>
        </td>
        <td>
            <div v-if="item.loadingDescription" class="smallLoader"></div>
            <div v-else>
                <div v-if="!item.descriptionId">{{ $t('planning.haveNo') }}</div>
                <div v-else>
                    <a :href="item.descriptionIsEvent ? `#/registers/events/edit/${item.descriptionId}` : `#/forms/details?id=${item.descriptionId}`"
                        target="_blank">
                        {{ item.descriptionName }}
                    </a>
                </div>
            </div>
        </td>
        <td>
            <span v-for="(lector, $index) of item.lectors" :title="lector.email">
                <span v-if="lector.firstName">
                    {{ lector.firstName }} {{ lector.lastName }} <i class="fas fa-trash text-danger pointer" @click="item.lectors.splice($index, 1)"></i>
                </span>
                <span v-else>
                    {{ lector.name }} <i class="fas fa-trash text-danger pointer" @click="item.lectors.splice($index, 1)"></i>
                </span>
                <hr v-if="$index !== item.lectors.length - 1">
            </span>
            <span class="float-right">
                <person-search :person-search-type="personSearchType.Lector"
                               :includeMergedProfiles="false"
                               @onPersonSelected="onLectorSelected($event, item)">
                </person-search>
            </span>
        </td>
        <td>
            <div v-if="item.loadingEvents" class="smallLoader"></div>
            <div v-else>
                <div v-if="(!item.eventsForTheme || item.eventsForTheme.length < 1) && (item.theme || item.themeId)"
                     class="text-right">
                    {{ $t('planning.haveNo') }}
                </div>
                <div v-for="(event, $index) of item.eventsForTheme">
                    <router-link :to="`/registers/events/edit/${event.id}`" target="_blank">
                        {{ event.name }}
                    </router-link>
                    <hr v-if="$index !== item.eventsForTheme.length - 1">
                </div>
            </div>
        </td>
        <td>
            <div v-if="item.loadingLectors" class="smallLoader"></div>
            <div v-else>
                <div v-if="(!item.lectorsForTheme || item.lectorsForTheme.length < 1) && (item.theme || item.themeId)"
                     class="text-right">
                    {{ $t('planning.haveNo') }}
                </div>
                <div v-for="lectorForTheme of item.lectorsForTheme">
                    {{ lectorForTheme.name}}
                    <button class="btn btn-secondary" @click="onLectorSelected(lectorForTheme, item)">{{ $t('planning.join') }}</button>
                </div>
            </div>
        </td>
    </tr>
</template>

<script>
    import {
        PlanningItemEventsForThemeModel,
        PlanningItemLectorsForThemeModel,
        PlanningItemModel
    } from '../../models/models';
    import { NomenclatureParentBySort, PersonSearchType } from '../../models/enums';
    import DynamicDropdown from '../dropdown/DynamicDropdown';
    import PersonSearch from '../common/PersonSearch';
    import NomenclatureDropdown from '../dropdown/NomenclatureDropdown';
    import Toggle from '../toggle/Toggle';

    export default {
        inject: ['$validator'],
        components: {
            DynamicDropdown,
            PersonSearch,
            NomenclatureDropdown,
            Toggle,
        },
        props: {
            item: {
                type: PlanningItemModel,
                default: new PlanningItemModel()
            },
            thematicAreasDropdownItems: {
                type: Array,
                require: true
            }
        },
        data() {
            return {
                personSearchType: PersonSearchType,
                nomenclature: NomenclatureParentBySort
            }
        },
        methods: {
            loadEventsForTheme() {
                this.item.loadingEvents = true;
                if (this.item.themeId) {
                    this.$http.get(`/api/Planning/GetEventsByThemeId/${this.item.themeId}`, {
                        theme: this.item.theme
                    }).then(result => {
                        this.item.loadingEvents = false;
                        if (result.data) {
                            result.data.forEach(item => {
                                this.item.eventsForTheme.push(new PlanningItemEventsForThemeModel(item));
                            });
                        }
                    }).catch(error => {
                        this.item.loadingEvents = false;
                    });
                } else {
                    this.$http.post(`/api/Planning/GetEventsByThemeName`, {
                        theme: this.item.alternativeName ? this.item.alternativeName : this.item.theme
                    }).then(result => {
                        this.item.loadingEvents = false;
                        if (result.data) {
                            result.data.forEach(item => {
                                this.item.eventsForTheme.push(new PlanningItemEventsForThemeModel(item));
                            });
                        }
                    }).catch(error => {
                        this.item.loadingEvents = false;
                    });
                }
            },
            loadLectorsForTheme() {
                this.item.loadingLectors = true;
                if (this.item.themeId) {
                    this.$http.get(`/api/Planning/GetLectorsByThemeId/${this.item.themeId}`).then(result => {
                        this.item.loadingLectors = false;
                        if (result.data) {
                            result.data.forEach(item => {
                                this.item.lectorsForTheme.push(new PlanningItemLectorsForThemeModel(item));
                            });
                        }
                    }).catch(error => {
                        this.item.loadingLectors = false;
                    });
                } else {
                    this.$http.post(`/api/Planning/GetLectorsByThemeName/`, {
                        theme: this.item.theme
                    }).then(result => {
                        this.item.loadingLectors = false;
                        if (result.data) {
                            result.data.forEach(item => {
                                this.item.lectorsForTheme.push(new PlanningItemLectorsForThemeModel(item));
                            });
                        }
                    }).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    }).then(() => this.loadingLectors = false);
                }

                return;
                this.$http.get(`/api/Planning/GetLectorsForTheme/${this.item.themeId}`).then(result => {
                    this.item.loadingLectors = false;
                    if (result.data) {
                        result.data.forEach(item => {
                            this.item.lectorsForTheme.push(new PlanningItemLectorsForThemeModel(item));
                        });
                    }
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                }).then(() => this.loadingLectors = false);
            },
            onLectorSelected(personSearchModel, item) {
                item.lectors.push(personSearchModel);
                item.proposedLectorsIds.push(personSearchModel.id);
            },
            loadLegalAreaForTheme(thematicAreaId) {
                if (!this.item.legalAreaId) {
                    this.$http.get(`/api/Planning/GetLegalAreaForTheme/${ thematicAreaId }`).then(result => {
                        this.item.legalAreaId = result.data;
                    }).catch(error => {
                        console.error(error);
                    });
                }
            },
            loadDescription() {
                if (this.item.formEntryId) {
                    this.item.loadingDescription = true;
                    this.$http.get(`/api/Planning/GetDescription/${this.item.formEntryId}`).then(result => {
                        this.item.loadingLectors = false;
                        if (result.data) {
                            this.item.descriptionName = result.data.descriptionName;
                            this.item.descriptionId = result.data.descriptionId;
                            this.item.descriptionIsEvent = result.data.descriptionIsEvent;
                        }
                        this.item.loadingDescription = false;
                    }).catch(error => {
                        this.item.loadingDescription = false;
                    });
                }
            }
        },
        mounted() {
            this.loadLectorsForTheme();
            this.loadEventsForTheme();
            this.loadDescription();
        },
        computed: {

        },
        watch: {
            'item.themeId': function (newVal, oldVal) {
                this.loadLectorsForTheme();
                this.loadEventsForTheme();
            },
            'item.thematicAreaId': function (newVal, oldVal) {
                this.loadLegalAreaForTheme(newVal);
            }
        }
    }
</script>
